import { post } from '../client'
import { setAuthToken, clearAuthToken, getAuthToken } from '../client'
import type {
  LoginRequest,
  LoginResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  AdminUser,
  ApiResponse,
  UserProfileResponse
} from '../types'

// Store current user and pending state
let currentUser: AdminUser | null = null
let pendingUserId: string | null = null

// Create admin user with all available details
function createAdminUser(profile: UserProfileResponse, fallbackEmail?: string): AdminUser {
  const fullName = profile.firstName && profile.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : profile.firstName || 'Admin User'

  return {
    id: profile.userId,
    firstName: profile.firstName,
    lastName: profile.lastName,
    email: profile.emailAddress || fallbackEmail || '',
    phoneNumber: profile.phoneNumber,
    profilePicture: profile.profilePicture,
    socialLinks: profile.socialLinks,
    isDefaultPassword: profile.isDefaultPassword,
    userRole: profile.userRole,
    otpChannel: profile.otpChannel,
    lastLoginWith2FA: profile.lastLoginWith2FA,
    hasActivatedAccount: profile.hasActivatedAccount,
    isTwoFactorEnabled: profile.isTwoFactorEnabled,
    fullName
  }
}

// Login function
export async function login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse> & { requires2FA: boolean }> {
  const response = await post<ApiResponse<LoginResponse>>('admins/public/authenticate', credentials)

  if (response.success && response.data) {
    const { token, userProfileResponse } = response.data

    if (token) {
      // 2FA is enabled - user is fully authenticated
      setAuthToken(token)

      const adminUser = createAdminUser(userProfileResponse, credentials.emailAddress)
      currentUser = adminUser
      sessionStorage.setItem('current_user', JSON.stringify(adminUser))

      return { ...response, requires2FA: false }
    } else {
      // 2FA is disabled - need OTP verification
      pendingUserId = userProfileResponse.userId
      sessionStorage.setItem('pending_user_id', userProfileResponse.userId)

      return { ...response, requires2FA: true }
    }
  }

  return { ...response, requires2FA: false }
}

// Verify OTP function
export async function verifyOtp(credentials: VerifyOtpRequest): Promise<ApiResponse<VerifyOtpResponse>> {
  const response = await post<ApiResponse<VerifyOtpResponse>>('/authenticate/verify-login-otp', credentials)

  if (response.success && response.data) {
    const { token, userProfileResponse } = response.data

    setAuthToken(token)

    const adminUser = createAdminUser(userProfileResponse, userProfileResponse.emailAddress)
    currentUser = adminUser
    sessionStorage.setItem('current_user', JSON.stringify(adminUser))

    // Clear pending state
    pendingUserId = null
    sessionStorage.removeItem('pending_user_id')
  }

  return response
}

// Logout function
export async function logout(): Promise<void> {
  try {
    // Call logout endpoint if needed
    // await post('/authenticate/logout')
  } catch (error) {
    console.warn('Logout API call failed:', error)
  } finally {
    // Clear all auth state
    currentUser = null
    pendingUserId = null
    clearAuthToken()
    sessionStorage.removeItem('current_user')
    sessionStorage.removeItem('pending_user_id')
  }
}

// Get current user
export function getCurrentUser(): AdminUser | null {
  if (!currentUser) {
    const stored = sessionStorage.getItem('current_user')
    if (stored) {
      try {
        currentUser = JSON.parse(stored)
      } catch {
        sessionStorage.removeItem('current_user')
      }
    }
  }
  return currentUser
}

// Check if authenticated
export function isAuthenticated(): boolean {
  const token = getAuthToken()
  const user = getCurrentUser()
  console.log('🔐 Auth Check:', { token: !!token, user: !!user, result: !!(token && user) })
  return !!(token && user)
}

// Get pending user ID for OTP
export function getPendingUserId(): string | null {
  if (!pendingUserId) {
    pendingUserId = sessionStorage.getItem('pending_user_id')
  }
  return pendingUserId
}

// Check user role
export function hasRole(role: string): boolean {
  const user = getCurrentUser()
  return user?.userRole === role
}

// Initialize auth on app start
export function initializeAuth(): void {
  const token = getAuthToken()
  if (token) {
    getCurrentUser() // This will restore from sessionStorage
  }
}